import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Search, ExternalLink, Mail, CheckCircle2, Clock, AlertCircle,
    DollarSign, CreditCard, Compass, Star, Crown, ChevronDown, ChevronUp,
    Calendar, Briefcase, MailOpen,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const TIER_META = {
    explorer: { icon: Compass, color: "text-fiji-green", bgClass: "bg-fiji-green/15", label: "Explorer" },
    investor: { icon: Star, color: "text-coral", bgClass: "bg-coral/15", label: "Investor" },
    developer: { icon: Crown, color: "text-ocean dark:text-turquoise", bgClass: "bg-ocean/15 dark:bg-turquoise/15", label: "Developer" },
};

const STATUS_META = {
    paid: { className: "bg-fiji-green/15 text-fiji-green border-fiji-green/30", label: "PAID", icon: CheckCircle2 },
    initiated: { className: "bg-secondary text-foreground border-border", label: "INITIATED", icon: Clock },
    pending: { className: "bg-coral/15 text-coral border-coral/30", label: "PENDING", icon: Clock },
    expired: { className: "bg-destructive/15 text-destructive border-destructive/30", label: "EXPIRED", icon: AlertCircle },
    open: { className: "bg-secondary text-foreground border-border", label: "OPEN", icon: Clock },
    complete: { className: "bg-fiji-green/15 text-fiji-green border-fiji-green/30", label: "COMPLETE", icon: CheckCircle2 },
};

export default function AdminConciergePurchasesTab() {
    const [data, setData] = useState({ items: [], stats: null });
    const [loading, setLoading] = useState(true);
    const [tierFilter, setTierFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [expandedId, setExpandedId] = useState(null);

    const reload = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (tierFilter !== "all") params.set("tier_id", tierFilter);
            if (statusFilter !== "all") params.set("status", statusFilter);
            const { data: d } = await api.get(`/admin/concierge-purchases?${params}`);
            setData(d);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not load purchases");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tierFilter, statusFilter]);

    const filtered = data.items.filter((p) => {
        if (!search.trim()) return true;
        const s = search.trim().toLowerCase();
        return (
            (p.email || "").toLowerCase().includes(s) ||
            (p.full_name || "").toLowerCase().includes(s) ||
            (p.country || "").toLowerCase().includes(s)
        );
    });

    return (
        <div data-testid="concierge-purchases-tab" className="space-y-6">
            {data.stats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="concierge-stats">
                    <StatTile
                        label="Total"
                        value={data.stats.total}
                        accent="ocean"
                    />
                    <StatTile
                        label="Paid"
                        value={data.stats.paid_count}
                        accent="fiji-green"
                        filterId="paid"
                        onClick={setStatusFilter}
                    />
                    <StatTile
                        label="Revenue"
                        value={`$${Number(data.stats.paid_revenue_usd || 0).toLocaleString()}`}
                        accent="coral"
                        prefix="USD"
                    />
                    <StatTile
                        label="Initiated"
                        value={data.stats.by_status?.initiated || 0}
                        accent="muted"
                        filterId="initiated"
                        onClick={setStatusFilter}
                    />
                    <StatTile
                        label="Expired"
                        value={data.stats.by_status?.expired || 0}
                        accent="destructive"
                        filterId="expired"
                        onClick={setStatusFilter}
                    />
                </div>
            )}

            {data.stats && (
                <div className="grid grid-cols-3 gap-3" data-testid="concierge-tier-stats">
                    {["explorer", "investor", "developer"].map((tid) => {
                        const meta = TIER_META[tid];
                        const Icon = meta.icon;
                        const count = data.stats.by_tier?.[tid] || 0;
                        return (
                            <Card
                                key={tid}
                                data-testid={`concierge-tier-stat-${tid}`}
                                className={`p-3 bg-card flex items-center gap-3 cursor-pointer hover:shadow-md transition border ${tierFilter === tid ? "border-coral/60" : "border-border"}`}
                                onClick={() => setTierFilter(tierFilter === tid ? "all" : tid)}
                            >
                                <div className={`h-9 w-9 rounded-md grid place-items-center ${meta.bgClass}`}>
                                    <Icon className={`h-4 w-4 ${meta.color}`} />
                                </div>
                                <div className="flex-1">
                                    <div className="text-[10px] uppercase tracking-wider font-app text-muted-foreground">
                                        {meta.label} tier
                                    </div>
                                    <div className="font-display text-lg font-bold tabular-nums text-foreground">{count}</div>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}

            <div className="flex flex-col lg:flex-row gap-3 lg:items-center justify-between">
                <div className="flex flex-wrap gap-2 items-center">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="h-9 w-40 bg-card text-foreground" data-testid="concierge-status-filter">
                            <SelectValue placeholder="All statuses" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="initiated">Initiated</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="expired">Expired</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={tierFilter} onValueChange={setTierFilter}>
                        <SelectTrigger className="h-9 w-40 bg-card text-foreground" data-testid="concierge-tier-filter">
                            <SelectValue placeholder="All tiers" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All tiers</SelectItem>
                            <SelectItem value="explorer">Explorer</SelectItem>
                            <SelectItem value="investor">Investor</SelectItem>
                            <SelectItem value="developer">Developer</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="relative max-w-xs">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        data-testid="concierge-search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search email, name, country…"
                        className="pl-8 h-9 bg-card text-foreground w-72"
                    />
                </div>
            </div>

            {loading ? (
                <div className="text-center text-muted-foreground font-app py-12" data-testid="concierge-loading">Loading purchases…</div>
            ) : filtered.length === 0 ? (
                <Card className="p-10 text-center bg-card" data-testid="concierge-empty">
                    <Briefcase className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                    <div className="font-display text-lg font-bold text-foreground mb-1">No purchases</div>
                    <p className="font-app text-sm text-muted-foreground">
                        Concierge tier purchases will appear here as soon as a Stripe checkout session is created.
                    </p>
                </Card>
            ) : (
                <div className="space-y-2">
                    {filtered.map((p) => (
                        <PurchaseRow
                            key={p.id}
                            purchase={p}
                            expanded={expandedId === p.id}
                            onToggle={() => setExpandedId(expandedId === p.id ? null : p.id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function StatTile({ label, value, accent = "ocean", prefix, filterId, onClick }) {
    const accents = {
        ocean: "border-ocean/30 text-ocean dark:text-turquoise",
        coral: "border-coral/30 text-coral",
        "fiji-green": "border-fiji-green/30 text-fiji-green",
        destructive: "border-destructive/30 text-destructive",
        muted: "border-border text-foreground",
    };
    const clickable = onClick && filterId;
    return (
        <Card
            className={`p-3 bg-card border ${accents[accent]} ${clickable ? "cursor-pointer hover:shadow-md transition" : ""}`}
            onClick={clickable ? () => onClick(filterId) : undefined}
            data-testid={`concierge-stat-${filterId || label.toLowerCase()}`}
        >
            <div className="text-[10px] uppercase tracking-wider font-app text-muted-foreground mb-0.5">
                {label}{prefix && <span className="ml-1">({prefix})</span>}
            </div>
            <div className="font-display text-2xl font-bold tabular-nums">{value}</div>
        </Card>
    );
}

function PurchaseRow({ purchase, expanded, onToggle }) {
    const tierMeta = TIER_META[purchase.tier_id] || TIER_META.explorer;
    const TierIcon = tierMeta.icon;
    const statusMeta = STATUS_META[purchase.payment_status] || STATUS_META.initiated;
    const StatusIcon = statusMeta.icon;

    return (
        <Card data-testid={`concierge-row-${purchase.id}`} className="bg-card overflow-hidden">
            <button
                type="button"
                onClick={onToggle}
                data-testid={`concierge-toggle-${purchase.id}`}
                className="w-full text-left p-4 hover:bg-secondary/40 dark:hover:bg-card/60 transition flex items-center gap-4"
            >
                <div className={`h-10 w-10 rounded-md grid place-items-center shrink-0 ${tierMeta.bgClass}`}>
                    <TierIcon className={`h-5 w-5 ${tierMeta.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Badge variant="outline" className={`font-app text-[10px] ${statusMeta.className}`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusMeta.label}
                        </Badge>
                        <Badge variant="outline" className="font-app text-[10px] border-coral/30 text-coral">
                            {tierMeta.label}
                        </Badge>
                        {purchase.welcome_email_sent && (
                            <Badge variant="outline" className="font-app text-[10px] border-fiji-green/30 text-fiji-green">
                                <MailOpen className="h-3 w-3 mr-1" /> welcomed
                            </Badge>
                        )}
                    </div>
                    <div className="font-display text-base font-bold text-foreground truncate">
                        {purchase.full_name || purchase.email}
                    </div>
                    <div className="text-xs font-app text-muted-foreground flex items-center gap-1.5 mt-0.5">
                        <Mail className="h-3 w-3" />
                        <span className="truncate">{purchase.email}</span>
                        {purchase.country && (
                            <>
                                <span className="text-muted-foreground/40">·</span>
                                <span>{purchase.country}</span>
                            </>
                        )}
                    </div>
                </div>
                <div className="hidden md:flex flex-col items-end shrink-0">
                    <div className="font-display text-lg font-bold tabular-nums text-foreground">
                        ${Number(purchase.amount_usd).toLocaleString()}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider font-app text-muted-foreground">USD</div>
                </div>
                <div className="flex flex-col items-end shrink-0 gap-1 hidden lg:flex">
                    <div className="text-[10px] uppercase tracking-wider font-app text-muted-foreground">Created</div>
                    <div className="text-xs font-app text-foreground">
                        {new Date(purchase.created_at).toLocaleDateString()}
                    </div>
                </div>
                {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
            </button>

            {expanded && (
                <div className="border-t border-border bg-secondary/20 dark:bg-card/30 p-4 space-y-3" data-testid={`concierge-detail-${purchase.id}`}>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <DetailField label="Stripe session" value={purchase.session_id} mono small />
                        <DetailField label="Payment status" value={purchase.payment_status} />
                        <DetailField label="Phone" value={purchase.phone || "—"} />
                        <DetailField label="Country" value={purchase.country || "—"} />
                        <DetailField label="Service ticket" value={purchase.service_ticket_id || "—"} mono small />
                        {purchase.investor_pipeline_id && (
                            <DetailField label="Investor pipeline" value={purchase.investor_pipeline_id} mono small />
                        )}
                        <DetailField label="Created" value={new Date(purchase.created_at).toLocaleString()} />
                        {purchase.paid_at && (
                            <DetailField label="Paid at" value={new Date(purchase.paid_at).toLocaleString()} />
                        )}
                        <DetailField label="Welcome email" value={purchase.welcome_email_sent ? "Sent" : "Not yet"} />
                    </div>
                    {purchase.notes && (
                        <Card className="p-3 bg-muted/40 dark:bg-card/60 border-border">
                            <div className="text-[10px] uppercase tracking-wider font-app text-muted-foreground font-bold mb-1">Buyer notes</div>
                            <p className="font-app text-sm text-foreground/90 whitespace-pre-wrap">{purchase.notes}</p>
                        </Card>
                    )}
                </div>
            )}
        </Card>
    );
}

function DetailField({ label, value, mono = false, small = false }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider font-app text-muted-foreground font-bold mb-1">{label}</div>
            <div className={`font-app text-foreground/90 ${mono ? "font-mono" : ""} ${small ? "text-xs break-all" : "text-sm"}`}>
                {value}
            </div>
        </div>
    );
}
